import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="chat-scroll"
export default class extends Controller {
  static targets = ["spacer", "typingIndicator", "messages"]

  connect() {
    this.scrollToBottom()
    this._userMessagePending = false
  }

  disconnect() {
    clearTimeout(this._pendingTimeout)
    this._stopSpacerObserver()
  }

  handleSubmitStart(event) {
    if (event.target.closest('#message_form')) {
      this._userMessagePending = true
      clearTimeout(this._pendingTimeout)
      this._pendingTimeout = setTimeout(() => { this._userMessagePending = false }, 5000)
    }
  }

  handleStreamRender(event) {
    const streamElement = event.detail.newStream
    if (!streamElement) return

    const action = streamElement.action
    const target = streamElement.getAttribute('target')

    if (!['append', 'before'].includes(action)) return
    if (target !== 'messages' && target !== 'typing_indicator') return

    if (this._userMessagePending) {
      this._userMessagePending = false
      clearTimeout(this._pendingTimeout)
      setTimeout(() => this._scrollUserMessageToTop(), 50)
    }
  }

  scrollToBottom() {
    this.element.scrollTo({
      top: this.element.scrollHeight,
      behavior: 'smooth'
    })
  }

  // --- Private ---

  _findLastUserMessage() {
    if (this.hasMessagesTarget) {
      return this.messagesTarget.lastElementChild
    }
    if (this.hasTypingIndicatorTarget) {
      // Chatbot widget: user message is right before the typing indicator
      return this.typingIndicatorTarget.previousElementSibling
    }
    return null
  }

  _scrollUserMessageToTop() {
    const targetElement = this._findLastUserMessage()
    const gap = 16 // 1rem — matches gap-4 between messages

    if (this.hasSpacerTarget) {
      const messageHeight = targetElement ? targetElement.offsetHeight : 0
      const indicatorHeight = this.hasTypingIndicatorTarget ? this.typingIndicatorTarget.offsetHeight : 0
      this.spacerTarget.style.minHeight = `${this.element.clientHeight - messageHeight - indicatorHeight - gap}px`
    }

    if (targetElement && targetElement.id !== 'chat_spacer') {
      const containerRect = this.element.getBoundingClientRect()
      const elementRect = targetElement.getBoundingClientRect()
      const scrollTop = this.element.scrollTop + (elementRect.top - containerRect.top) - gap

      this.element.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      })
    }

    this._startSpacerObserver()
  }

  _startSpacerObserver() {
    if (!this.hasSpacerTarget) return

    this._stopSpacerObserver()

    const spacer = this.spacerTarget
    const containerHeight = this.element.clientHeight
    const initialScrollHeight = this.element.scrollHeight
    let spacerReduction = 0

    const updateSpacer = () => {
      this._rafPending = false
      const totalGrowth = this.element.scrollHeight - initialScrollHeight + spacerReduction
      const newSpacerHeight = Math.max(0, containerHeight - totalGrowth)
      const newReduction = containerHeight - newSpacerHeight

      if (newReduction !== spacerReduction) {
        spacerReduction = newReduction
        spacer.style.minHeight = `${newSpacerHeight}px`
      }

      if (newSpacerHeight === 0) {
        this._stopSpacerObserver()
      }
    }

    this._spacerObserver = new MutationObserver(() => {
      if (!this._rafPending) {
        this._rafPending = true
        requestAnimationFrame(updateSpacer)
      }
    })

    this._spacerObserver.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true
    })
  }

  _stopSpacerObserver() {
    if (this._spacerObserver) {
      this._spacerObserver.disconnect()
      this._spacerObserver = null
    }
  }
}
